import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  Input,
} from '@fluentui/react-components';
import { WarningRegular } from '@fluentui/react-icons';
import { type UseMutationResult } from '@tanstack/react-query';

export interface CashPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recordCashPaymentMutation: UseMutationResult<unknown, Error, { amount: number; reference?: string }>;
}

export function CashPaymentDialog({
  open,
  onOpenChange,
  recordCashPaymentMutation,
}: CashPaymentDialogProps) {
  const [cashPaymentAmount, setCashPaymentAmount] = useState('');
  const [cashPaymentReference, setCashPaymentReference] = useState('');

  const handleCancel = () => {
    onOpenChange(false);
    setCashPaymentAmount('');
    setCashPaymentReference('');
  };

  const handleSubmit = () => {
    const amount = parseFloat(cashPaymentAmount);
    if (amount > 0) {
      recordCashPaymentMutation.mutate(
        {
          amount,
          reference: cashPaymentReference || undefined,
        },
        {
          onSuccess: () => {
            onOpenChange(false);
            setCashPaymentAmount('');
            setCashPaymentReference('');
          },
        }
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Record Cash Payment</DialogTitle>
          <DialogContent>
            <p style={{ marginBottom: '16px', color: 'var(--tl-text-secondary)' }}>
              Record a cash payment for this tax return. This will NOT create an invoice in Xero.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Field label="Amount" required>
                <Input
                  type="number"
                  value={cashPaymentAmount}
                  onChange={(_, data) => setCashPaymentAmount(data.value)}
                  placeholder="0.00"
                  contentBefore={<span>$</span>}
                />
              </Field>
              <Field label="Reference (Optional)">
                <Input
                  value={cashPaymentReference}
                  onChange={(_, data) => setCashPaymentReference(data.value)}
                  placeholder="e.g., receipt number"
                />
              </Field>
            </div>
            {recordCashPaymentMutation.isError && (
              <div style={{ color: '#d83b01', fontSize: '13px', marginTop: '8px' }}>
                <WarningRegular /> {(recordCashPaymentMutation.error as Error)?.message || 'Failed to record cash payment'}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!cashPaymentAmount || parseFloat(cashPaymentAmount) <= 0 || recordCashPaymentMutation.isPending}
              onClick={handleSubmit}
            >
              {recordCashPaymentMutation.isPending ? 'Recording...' : 'Record Cash Payment'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
